"use client"

import { ControlPosition, Map as GoogleMap } from "@vis.gl/react-google-maps"
import { useLocale, useTranslations } from "next-intl"
import { useCallback, useMemo, useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import { getPortsWithCoordinates, PortLocation } from "utils"
import MapBoundsHandler from "../MapBoundsHandler"
import PortMarker from "../PortMarker"
import PortSlider from "./PortSlider"
import { PortMapContentProps } from "./types"

const PortMapContent = ({ ports, title, isDesktop }: PortMapContentProps) => {
  const locale = useLocale()
  const t = useTranslations("PortMapLocationsOnMap")

  const textDirection: "ltr" | "rtl" = locale === "he" ? "rtl" : "ltr"
  const swiperDirection = locale === "he" ? (isDesktop ? "ltr" : "rtl") : "ltr"

  // Filter ports to only those with valid coordinates from the map field
  const portLocations: PortLocation[] = useMemo(() => getPortsWithCoordinates(ports), [ports])

  // Extract just the ports that have coordinates for the slider
  const portsWithCoordinates = useMemo(
    () => portLocations.map((location) => location.port),
    [portLocations],
  )

  const [activePortIndex, setActivePortIndex] = useState<number | null>(null)
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null)

  const handleSwiperReady = useCallback((swiper: SwiperType) => {
    setSwiperInstance(swiper)
  }, [])

  const handleMarkerClick = useCallback(
    (port: PortLocation["port"]) => {
      const portIndex = portsWithCoordinates.findIndex((p) => p.url === port.url)
      if (portIndex !== -1) {
        setActivePortIndex(portIndex)
        if (swiperInstance) {
          if (isDesktop) {
            const slideElement = swiperInstance.slides[portIndex] as HTMLElement
            if (slideElement) {
              const container = swiperInstance.el
              const slideTop = slideElement.offsetTop
              const containerHeight = container.clientHeight
              const slideHeight = slideElement.offsetHeight
              const scrollTarget = slideTop - (containerHeight - slideHeight) / 2
              container.scrollTo({
                top: Math.max(0, scrollTarget),
                behavior: "smooth",
              })
            }
          } else {
            swiperInstance.slideTo(portIndex)
          }
        }
      }
    },
    [portsWithCoordinates, swiperInstance, isDesktop],
  )

  const defaultCenter = { lat: 31.4117257, lng: 35.0818155 }

  return (
    <>
      <GoogleMap
        mapId="DEMO_MAP_ID"
        mapTypeControl={false}
        gestureHandling={"cooperative"}
        disableDefaultUI={true}
        clickableIcons={false}
        zoomControl={true}
        zoomControlOptions={{
          position: isDesktop ? ControlPosition.BOTTOM_LEFT : ControlPosition.TOP_LEFT,
        }}
        defaultCenter={defaultCenter}
        defaultZoom={8}
      >
        <MapBoundsHandler
          locations={portLocations}
          padding={
            isDesktop
              ? { top: 100, right: 100, bottom: 100, left: 600 }
              : { top: 60, right: 30, bottom: 280, left: 30 }
          }
          maxZoom={10}
        />

        {portLocations.map((location) => (
          <PortMarker
            key={location.port.url}
            port={location.port}
            position={{ lat: location.lat, lng: location.lng }}
            onMarkerClick={handleMarkerClick}
            isSelected={
              activePortIndex !== null &&
              portsWithCoordinates[activePortIndex].url === location.port.url
            }
          />
        ))}
      </GoogleMap>

      <PortSlider
        ports={portsWithCoordinates}
        direction={textDirection}
        swiperDirection={swiperDirection}
        isDesktop={isDesktop}
        openInWazeLabel={t("openInWaze")}
        publicTransportationLabel={t("publicTransportationLabel")}
        parkingLabel={t("parkingLabel")}
        headerTitle={title || t("locations")}
        activePortIndex={activePortIndex}
        onSwiperReady={handleSwiperReady}
      />
    </>
  )
}

export default PortMapContent
